
import { Grid, GridColumn as Column, GridCustomCellProps, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { circleIcon } from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../../messages/en-US';
import { State, process } from '@progress/kendo-data-query';
import { useState } from 'react';
import { ClientRoutes } from '../../../../../Constants/ClientRoutingConstants';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestion } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { GetGatewayIcon, GetGatewayHMEIcon } from "../../../../../Utilities/CustomIcons";
import { formatSmartDate } from "../../../../Shared/Utilities/DateFormattingUtils";

interface Props {
  healthData: any;
}

export default function GatewaysHealthTable({ healthData }: Props) {

  const navigate = useNavigate();
  const localizationService = useLocalization();
  
  const ConnectivityCell = (props: any) => {
    return <td>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="large" className={getStatusColor(props.dataItem.connectivityStatus)} />
        <span>{props.dataItem.connectivityStatus}</span>
      </div>
    </td>;
  };

  const getStatusColor = (status: string) => {

    switch (status) {
      case 'Healthy':
      case 'Online':
      case 'Enabled':
        return 'text-gbBrand-green-700';
      case 'Unhealthy':
      case 'Offline':
        return 'text-gbBrand-red-500';
      case 'Warning':
        return 'text-gbBrand-yellow-500';
      case 'Disabled':
        return 'text-gbBrand-grey-300';
      default:
        return 'text-gbBrand-blue-500';
    }
  };

  const LastActivityCell = (props: GridCustomCellProps) => {
    const localizationService = useLocalization();
    const { displayText: lastSeenText, tooltipText } = formatSmartDate(
      props.dataItem.lastActivity,
      localizationService,
      enMessages
    );

    return <td title={tooltipText}>
      {lastSeenText}
    </td>;
  };

  const TypeCell = (props: GridCustomCellProps) => {
    let tooltipText = "";
    if (props.dataItem.deviceType === "Gateway") {
      tooltipText = `Gateway`;
    } else if (props.dataItem.deviceType === "GatewayHME") {
      tooltipText = `Gateway HME`;
    } else {
      tooltipText = localizationService.toLanguageString('custom.unknownType', enMessages.custom.unknownType);
    }

    return (
      <td>
        <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              cursor: "default",
            }}
            title={tooltipText}
          >
            {props.dataItem.deviceType === "Gateway" ? (
              GetGatewayIcon(18, 18)
            ) : 
            props.dataItem.deviceType === "GatewayHME" ? (
              GetGatewayHMEIcon(18, 18)
            ) : (
              <FontAwesomeIcon icon={faQuestion} size="lg" />
            )}
          </span>
        </Tooltip>
      </td>
    );
  };

  const NameCell = (props: any) => {
    return <td>
      <button 
        className="text-left text-blue-600 underline cursor-pointer"
        onClick={() => navigate(`${ClientRoutes.HealthDashboardPage}/${props.dataItem.id}`, { 
          state: { 
            deviceData: props.dataItem, 
            deviceType: "gateway",
            selectedTile: "gateways"
          } 
        })}
      >
        {props.dataItem.serialNumber}
      </button>
    </td>;
  };

  // Initialize data state for virtual scrolling
  const [dataState, setDataState] = useState<State>({
    skip: 0,
    take: 30,
    sort: [{ field: 'connectivityStatus', dir: 'asc' }],
    filter: {
      logic: 'and',
      filters: []
    }
  });

  // Handle data state changes (pagination, sorting, filtering)
  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  // Process the data with current state
  const result = process(healthData, dataState);

  const FirmwareVersionCell = (props: any) => {
    return <td>{props.dataItem.firmwareVersion !== '' ? props.dataItem.firmwareVersion : '-'}</td>;
  };

  const GreasePumpEnabledCell = (props: GridCustomCellProps) => {
    return <td>
    <div className="flex items-center gap-2">
      <SvgIcon icon={circleIcon} size="large" className={getStatusColor(props.dataItem.greasePumpEnabled ? 'Enabled' : 'Disabled')} />
      <span>{props.dataItem.greasePumpEnabled ? localizationService.toLanguageString('custom.enabled', enMessages.custom.enabled) : localizationService.toLanguageString('custom.disabled', enMessages.custom.disabled)}</span>
    </div>
  </td>;
  };

  return (
    <>
      <div className="text-xl font-bold p-4">{localizationService.toLanguageString('custom.gatewaysHealth', enMessages.custom.gatewaysHealth)}</div>
      <Grid
        style={{ height: 'calc(100vh - 345px)', margin: '0 10px 10px 10px' }}
        rowHeight={46}
        data={result.data}
        total={result.total}
        skip={dataState.skip}
        take={dataState.take}
        scrollable="virtual"
        sortable={true}
        filterable={true}
        onDataStateChange={dataStateChange}
        {...dataState}
      >
        <Column field="deviceType" title=" " cells={{data: TypeCell}} width="45px" filterable={false} />
        <Column field="serialNumber" title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)} cells={{data: NameCell}} />
        <Column field="connectivityStatus" title={localizationService.toLanguageString('custom.connectivity', enMessages.custom.connectivity)} cells={{data: ConnectivityCell}} />
        <Column field="greasePumpEnabled" title={localizationService.toLanguageString('custom.greasePumpConnectivity', enMessages.custom.greasePumpConnectivity)} cells={{data: GreasePumpEnabledCell}} />
        <Column field="inactivityTimeoutInMinutes" title={localizationService.toLanguageString('custom.inactivityTimeoutInMinutes', enMessages.custom.inactivityTimeoutInMinutes)} />
        <Column field="lastActivity" title={localizationService.toLanguageString('custom.lastActivity', enMessages.custom.lastActivity)} cells={{data: LastActivityCell}} />
        <Column field="firmwareVersion" title={localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion)} cells={{data: FirmwareVersionCell}} />
      </Grid>
    </>
  );
}
